/** @jsx jsx */
import { colors } from '@apollo/brand';
import IconGraphQL from '@apollo/icons/large/IconGraphQL.svg';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import Avatar from 'boring-avatars';
import classnames from 'classnames';

export const MarbledAvatar = ({
  size = 48,
  identifier,
  className,
  Icon,
  animate,
}: {
  identifier: string;
  size?: number;
  className?: string;
  Icon?: React.ReactElement;
  animate: boolean;
}) => {
  return (
    <div
      style={{ width: size, height: size }}
      className={classnames(className, 'relative')}
    >
      <div
        css={
          animate &&
          css`
            border: 1px solid ${customColors.border.primary};
            border-radius: 100%;
            height: fit-content;
            width: fit-content;
            display: flex;
            svg path {
              animation: rotation-effect 20s cubic-bezier(0, 0.4, 1, 0.6) 2;
              animation-delay: -5s;
            }
            @keyframes rotation-effect {
              0% {
                transform: translateX(-80px);
              }
              100% {
                transform: translateX(80px);
              }
            }
          `
        }
        /**
         * Challenges:
         *
         * I need some way of duplicating the fill contents. If the fill was a background, we could use the CSS background-repeat property...but it's not. If the fill was a SVG <linearGradient> we could use spreadMethod set to "repeat" or "reflect"...but it's not. Since this is a group of objects overlaid with a blur filter to simulate an SVG gradient, I'm not sure the rotation effect is possible with this current approach.
         * - Solution: looks like just moving the class for the animation down one level to the paths (but not the rect) solves this by making the rect the background canvas.
         *
         * Other approaches:
         * - using the SVG <animate> object to manually animate the position of the content values (instead of css?)...but what about the repeating of the contents? (status: solved with CSS animation)
         * - reproducing with CSS and just using the SVG object as a background to repeat/reflect? (would need to reflect though; repeat may still show the edges where it doesn't match exactly on either side)
         *
         * Examples using CSS background on repeat:
         * - https://w3bits.com/css-earth/
         * - https://codepen.io/josue_26/pen/dojbOG?editors=1100
         *
         * Other useful articles:
         * - https://www.smashingmagazine.com/2015/12/animating-clipped-elements-svg/
         * - https://www.visualcinnamon.com/2016/05/animate-gradient-imitate-flow-d3/
         * - https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/spreadMethod
         * - https://css-tricks.com/reversing-an-easing-curve/
         * - https://developer.mozilla.org/en-US/docs/Web/CSS/easing-function
         * - https://developer.mozilla.org/en-US/docs/Web/CSS/animation-timing-function
         * - https://css-tricks.com/starting-css-animations-mid-way/
         * - https://developer.mozilla.org/en-US/docs/Web/SVG/SVG_animation_with_SMIL
         *
         * Possible solutions:
         *
         * 1. Animate just the path elements over the rect with CSS animate or SVG animate. (IMPLEMENTED HERE CURRENTLY)
         * Outstanding issues:
         * - there is a long time when the canvas is just blank showing the rectangle since making the animation seamless relies on starting from a negative translateX value setting it off the screen at the 0% keyframe, which means the avatars will load just as a solid color rather than the whole gradient, which is less than ideal. Ideally they could load as the whole gradient and rotate seamlessly from there, but this would require something like having a translateX starting point of 0 and then going to 80px, and then starting a new loop of -80px to 80px indefinitely from there.
         *   - Solved: using animation timing delay set to negative value for loading state as normal, then animate
         *   - Solved: using a cubic-bezier animation timing function that replicates ease-in-out (but in reverse) seems to get the start/end point back a bit faster to avoid the very long time with just the rect showing
         * Outstanding issues:
         * - This approach relies on injecting the class for the rotation animation into the paths inside the SVG group.
         *   - Possible solution: Maybe that's not a problem though? I guess we could just modify the library a bit to include this class on the paths?
         *
         * For further iteration:
         * - The effect could be improved by generating a double wide shape that could be tiled seamlessly. That could also simply be used as a CSS background image, and then background-position could be animated with CSS rather than needing to insert a class on the inner-html of the SVG objects (similar to the globe approach here: https://www.smashingmagazine.com/2015/12/animating-clipped-elements-svg/)
         */
      >
        <Avatar
          size={size}
          name={identifier}
          variant="marble"
          colors={[
            colors.tokens.brand.primary.dark,
            colors.tokens.brand.primary.base,
            colors.tokens.brand.secondary.base,
            colors.tokens.brand.secondary.dark,
          ]}
        />
      </div>
      {Icon || (
        <IconGraphQL className="absolute left-1/4 top-1/4 size-1/2 text-icon-white" />
      )}
    </div>
  );
};
